import React, { useEffect, useState, useMemo } from 'react'
import { Container, Row, Col, Card, Table, Form, Modal, Button } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import useAuth from '../../../context/auth/useAuth'
import { getBalance } from '../../../actions/balancesActions'
import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
import 'react-calendar/dist/Calendar.css'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import BalanceExportPDF from './BalanceExportPDF'
import { use } from 'i18next'

export default function Balance () {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const {client} = useAuth()

    const [dateSelected, setDateSelected] = useState([null, null])
    const [showPreview, setShowPreview] = useState(false)
    const {balance, initialBalance, loading} = useSelector(state=>state.balances)

    useEffect(() => {
        if(client){
            dispatch(getBalance())
        }
    },[dispatch, t, client])

    const handlePeriodChange = (value) => {
        setDateSelected([value ? value[0] : null, value ? value[1] : null])

    }

    useEffect(() => {
        dispatch(getBalance(dateSelected[0], dateSelected[1]))
    }, [dateSelected])

    const kgTon = (kg) => {
        return (kg/1000).toFixed(2)
    }

      // Memo para no recalcular el PDF dos veces (viewer y download)
    const pdfDoc = useMemo(() => (
        <BalanceExportPDF
        balance={balance}
        initialBalance={initialBalance}
        t={t}
        />
    ), [balance, initialBalance, t])

    return (
        <Container className='mt-5 px-0'>
            <Row>
                <Col xs={12} className='text-center'>
                    <h1>{t('admin.movements.balance')}</h1>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={4} className='my-2'>
                    <Button
                        variant="primary"
                        onClick={() => setShowPreview(true)}
                        disabled={loading}
                    >
                        {loading ? t('global.loading') ?? 'Cargando…' : t('global.preview') ?? 'Vista previa / Exportar'}
                    </Button>
                </Col>
                <Col xs={12} md={4} className='my-2 ms-auto'>
                    <Form.Group controlId="selectDate">
                        <Form.Label>{t('admin.movements.selectDate')}</Form.Label>
                        <DateRangePicker
                            className="has-content form-control"
                            onChange={(value) => {handlePeriodChange(value)}}
                            value={dateSelected ? [dateSelected[0], dateSelected[1]] : [null, null]}
                            required
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className='text-end'>
                    <h2>{t('admin.movements.initialBalance')}: {kgTon(initialBalance)} {t("global.toneladasbruto")}</h2>
                </Col>
            </Row>
            <Row>
                {(() => {
                    let accumulatedBalance = initialBalance
                    return balance?.map((monthData, index) => {
                        const monthBalance = monthData.totalEntries - monthData.totalOutputs
                        accumulatedBalance += monthBalance
                        return (
                        <Col xs={12} key={index}>
                            <Card className='my-3'>
                                <Card.Header>
                                    <h2>{t("months."+monthData.month)} {monthData.year}</h2>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col xs={12} md={2}>
                                            <h3>{t("admin.movements.entries")}</h3>
                                            <h4>{kgTon(monthData.totalEntries)} {t("global.toneladasbruto")}</h4>
                                        </Col>
                                        <Col xs={12} md={5}>
                                            <h3>{t("admin.movements.outputs")}</h3>
                                            <h4>{kgTon(monthData.totalOutputs)} {t("global.toneladasbruto")}</h4>
                                        </Col>
                                        <Col xs={12} md={5}>
                                            <h3>{t("admin.movements.wastes")}</h3>
                                            <h4>{kgTon(monthData.totalWastes)} {t("global.toneladasbruto")}</h4>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12} md={2}>
                                        </Col>
                                        <Col xs={12} md={5}>
                                        {monthData.outputs.length > 0 ? (
                                            <Table className='table table-striped'>
                                                <thead>
                                                    <tr>
                                                        <td>{t("admin.movements.date")}</td>
                                                        <td>{t("admin.movements.nature")}</td>
                                                        <td>{t("admin.movements.business")}</td>
                                                        <td>{t("global.toneladasbruto")}</td>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {monthData.outputs.sort((a, b) => new Date(a.date) - new Date(b.date)).map((output) => (
                                                    <tr key={output._id}>
                                                        <td>{`${new Date(output.date).getDate().toString().padStart(2, '0')}/${(new Date(output.date).getMonth() + 1).toString().padStart(2, '0')}/${new Date(output.date).getFullYear()}`}</td>
                                                        <td>{output.nature}</td>
                                                        <td>{output.business}</td>
                                                        <td className='text-end'>{kgTon(output.quantity)}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <p>{t("admin.movements.thereisnotmovements")}.</p>
                                        )}
                                        </Col>
                                        <Col xs={12} md={5}>
                                        {monthData.wastes.length > 0 ? (
                                            <Table className='table table-striped'>
                                                <thead>
                                                    <tr>
                                                        <td>{t("admin.movements.date")}</td>
                                                        <td>{t("admin.movements.nature")}</td>
                                                        <td>{t("admin.movements.business")}</td>
                                                        <td>{t("global.toneladasbruto")}</td>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                {monthData.wastes.sort((a, b) => new Date(a.date) - new Date(b.date)).map((waste) => (
                                                    <tr key={waste._id}>
                                                        <td>{`${new Date(waste.date).getDate().toString().padStart(2, '0')}/${(new Date(waste.date).getMonth() + 1).toString().padStart(2, '0')}/${new Date(waste.date).getFullYear()}`}</td>
                                                        <td>{waste.nature}</td>
                                                        <td>{waste.business}</td>
                                                        <td className='text-end'>{kgTon(waste.quantity)}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <p>{t("admin.movements.thereisnotmovements")}.</p>
                                        )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                                <Card.Footer>
                                    <Row>
                                        <Col className='text-end'>
                                            {t("admin.movements.balanceMonth")} {kgTon(monthBalance)} {t("global.toneladasbruto")}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className='text-end'>
                                            <strong>{t("admin.movements.accumulated")} {kgTon(accumulatedBalance)} {t("global.toneladasbruto")}</strong>
                                        </Col>
                                    </Row>
                                </Card.Footer>
                            </Card>
                        </Col>
                        )
                    })
                })()}
            </Row>
            <Modal
                show={showPreview}
                onHide={() => setShowPreview(false)}
                size="xl"
                centered
                dialogClassName="modal-xxl"
            >
                <Modal.Body style={{ height: '75vh', padding: 0 }}>
                <div style={{ width: '100%', height: '100%' }}>
                    <PDFViewer width="100%" height="100%">
                    {pdfDoc}
                    </PDFViewer>
                </div>
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                    <PDFDownloadLink document={pdfDoc} fileName="balance.pdf">
                    {({ loading }) => (
                        <Button variant="primary" disabled={loading}>
                        {loading ? 'Generando PDF…' : 'Exportar'}
                        </Button>
                    )}
                    </PDFDownloadLink>
                    <Button variant="primary" onClick={() => setShowPreview(false)}>
                    {t('global.close')}
                    </Button>
                </div>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}